import { Redirect, Stack, useLocalSearchParams } from 'expo-router';
import { ActivityIndicator, ScrollView, View } from 'react-native';

import { PageHeaderRow } from '@/components/page-header-row';
import { useTenant } from '@/components/tenant-provider';
import { InstagramConnection } from '@/components/integrations/instagram-connection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';
import { useTenantOwnerAccess } from '@/lib/use-tenant-owner-access';

export default function TenantInstagramIntegrationScreen() {
  const { tenant, connected, error } = useLocalSearchParams<{
    tenant: string;
    connected?: string;
    error?: string;
  }>();
  const { activeTenant } = useTenant();
  const ownerAccess = useTenantOwnerAccess();

  const tenantId = activeTenant?.id;

  if (ownerAccess.isLoading) {
    return (
      <>
        <Stack.Screen
          options={{ headerShown: process.env.EXPO_OS !== 'web', title: 'Instagram' }}
        />
        <Stack.Screen.BackButton displayMode="minimal">Integrations</Stack.Screen.BackButton>
        <View className="flex-1 items-center justify-center bg-background">
          <ActivityIndicator />
        </View>
      </>
    );
  }

  if (!ownerAccess.isAllowed) {
    return <Redirect href={{ pathname: '/admin/[tenant]', params: { tenant } }} />;
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: process.env.EXPO_OS !== 'web', title: 'Instagram' }} />
      <Stack.Screen.BackButton displayMode="minimal">Integrations</Stack.Screen.BackButton>
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
      >
        <PageHeaderRow
          backHref={{
            pathname: '/admin/[tenant]/settings/integrations',
            params: { tenant },
          }}
          subtitle={activeTenant?.name ?? 'Your club'}
          title="Instagram"
        />
        {process.env.EXPO_OS !== 'web' ? (
          <Text variant="muted">{activeTenant?.name ?? 'Your club'}</Text>
        ) : null}

        {tenantId ? (
          <InstagramConnection
            ownerQuery={`tenant_id=${encodeURIComponent(tenantId)}`}
            returnPath={`/admin/${tenant}/integrations/instagram`}
            description="Link this club to an Instagram professional account (Business or Creator). For 6am.bkk, that would typically be @6am.bkk."
            connected={connected}
            error={error}
          />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <Text variant="muted">No active tenant.</Text>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Setup checklist</CardTitle>
            <CardDescription>One-time Meta developer setup (see docs)</CardDescription>
          </CardHeader>
          <CardContent className="gap-2">
            <Text variant="small">1. Create a Meta Business app with Instagram API.</Text>
            <Text variant="small">
              2. Add Instagram Login and request instagram_business_basic.
            </Text>
            <Text variant="small">
              3. Set redirect URI to your /api/integrations/instagram/callback.
            </Text>
            <Text variant="small">4. Add env vars to .env.local and restart the dev server.</Text>
            <Text variant="small">
              5. Convert @6am.bkk to a professional Instagram account if needed.
            </Text>
            <Text variant="small" className="text-muted-foreground">
              Full steps: docs/integrations/instagram.mdx in this repo.
            </Text>
          </CardContent>
        </Card>
      </ScrollView>
    </>
  );
}
